import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DollarSign, RefreshCw, ToggleLeft, ToggleRight } from 'lucide-react';
import currencyService from '../services/currencyService';

const CurrencyDisplay = () => {
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBuyRates, setShowBuyRates] = useState(false); // varsayılan: satış kurları
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [updateKey, setUpdateKey] = useState(0); // UI güncelleme zorlamak için
  const panelRef = useRef(null);

  // Döviz kurlarını getir
  const fetchRates = useCallback(async () => {
    try {
      setIsRefreshing(true);
      setError(null); // Hata durumunu temizle ama mevcut veriyi koru
      
      const data = await currencyService.fetchCurrencyRates();
      
      if (data && Object.keys(data).length > 0) {
        // Yeni obje referansı oluştur (React state güncellemesini force et)
        setRates({...data});
        setUpdateKey(prev => prev + 1); // UI güncelleme force et
        // success
        
        // Loading durumunu kapat
        if (loading) {
          setLoading(false);
        }
      } else {
        throw new Error('Boş veri alındı');
      }
    } catch (err) {
      console.error('Döviz kurları hatası:', err);
      
      // Mobil cihazlarda daha kullanıcı dostu hata mesajları
      let errorMessage = 'Kurlar alınamadı';
      if (err.message.includes('Failed to fetch') || err.message.includes('Network')) {
        errorMessage = 'İnternet bağlantısı sorunu';
      } else if (err.message.includes('timeout') || err.name === 'AbortError') {
        errorMessage = 'Bağlantı zaman aşımı';
      } else if (err.message.includes('HTTP error')) {
        errorMessage = 'Sunucu hatası';
      }
      
      setError(errorMessage);
      
      // İlk yüklemede loading'i kapat, sonraki hatalarında mevcut veriyi koru
      if (loading) {
        setLoading(false);
      }
      // rates state'ini koruyoruz - eski veriler kalıyor
    } finally {
      setIsRefreshing(false);
    }
  }, [loading]);

  // İlk yükleme
  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  // Mobil cihazlar için 10 dakikada bir otomatik yenileme
  useEffect(() => {
    const interval = setInterval(fetchRates, 10 * 60 * 1000); // 10 dakika (mobil için uzatıldı)
    return () => clearInterval(interval);
  }, [fetchRates]);

  // Dış tıklama kontrolü (mobil cihaz desteği ile)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Mobil cihazlar için hem mouse hem touch eventları
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Kuru formatla - aşağı yuvarla
  const formatRate = (rate) => {
    if (!rate) return '-.--';
    // 2 basamağa aşağı yuvarla
    const rounded = Math.floor(rate * 100) / 100;
    return rounded.toFixed(2);
  };

  // Badge için kısa format (2 basamak) - aşağı yuvarla
  const formatRateShort = (rate) => {
    if (!rate) return '-.--';
    // 2 basamağa aşağı yuvarla
    const rounded = Math.floor(rate * 100) / 100;
    return rounded.toFixed(2);
  };

  // Zaman formatı (Harem API zaten Türkiye saatinde gönderiyor)
  const formatTurkishTime = (timestamp) => {
    if (!timestamp) return '';
    
    // API'den gelen format: "2025-09-02 14:05:01" (zaten Türkiye saati)
    // Sadece saat kısmını al ve göster
    try {
      if (timestamp.includes(' ')) {
        const timePart = timestamp.split(' ')[1]; // "14:05:01"
        return timePart;
      }
      
      // Fallback - eğer farklı format gelirse
      return timestamp;
    } catch (error) {
      console.error('Timestamp parse hatası:', error);
      return timestamp;
    }
  };

  // Switch toggle
  const toggleRateType = () => {
    setShowBuyRates(!showBuyRates);
  };

  // Yenile butonu - cache'i bypass et
  const handleRefresh = () => {
    if (!isRefreshing) {
      // Cache'i temizle ve yeni veri çek
      currencyService.clearCache();
      fetchRates();
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Dropdown Button - Diğer header butonlarıyla uyumlu */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors relative"
        title={loading ? 'Kurlar yükleniyor...' : error ? 'Hata oluştu' : rates.USDTRY ? `USD/TRY: ${formatRateShort(rates.USDTRY.sell)} - ${formatTurkishTime(rates.USDTRY.timestamp)}` : 'Döviz kurları'}
      >
        <DollarSign className="w-5 h-5" />
        
        {/* Status indicator */}
        {loading && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
        )}
        
        {error && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
        )}
        
        {!loading && !error && rates.USDTRY && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full"></span>
        )}
      </button>

      {/* Dropdown Panel - Mobil Uyumlu Design */}
      {isOpen && (
        <div className="fixed sm:absolute right-2 sm:right-0 top-16 sm:top-auto sm:mt-2 w-[calc(100vw-1rem)] sm:w-64 md:w-72 max-w-[calc(100vw-1rem)] sm:max-w-none bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
          <div className="p-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Döviz Kurları
              </h3>
              <div className="flex items-center space-x-1">
                {/* Alış/Satış Switch */}
                <button
                  onClick={toggleRateType}
                  className="flex items-center px-1.5 py-0.5 rounded text-xs hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title={showBuyRates ? 'Alış kurları gösteriliyor' : 'Satış kurları gösteriliyor'}
                >
                  {showBuyRates ? (
                    <ToggleRight className="w-3 h-3 text-orange-500 mr-1" />
                  ) : (
                    <ToggleLeft className="w-3 h-3 text-green-500 mr-1" />
                  )}
                  <span className="text-gray-600 dark:text-gray-400">
                    {showBuyRates ? 'A' : 'S'}
                  </span>
                </button>
                
                {/* Yenile butonu */}
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  title="Kurları yenile"
                >
                  <RefreshCw className={`w-3 h-3 text-gray-500 dark:text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* Hata bildirimi - üstte göster */}
            {error && (
              <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-xs text-red-600 dark:text-red-400 text-center">
                  {error}
                </p>
              </div>
            )}
            
            {loading ? (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Yükleniyor...</p>
              </div>
            ) : Object.keys(rates).length === 0 ? (
              <div className="text-center py-4">
                <p className="text-sm text-red-600 dark:text-red-400 mb-2">Hiç veri yok</p>
                <button
                  onClick={handleRefresh}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Tekrar dene
                </button>
              </div>
            ) : (
              <div className="space-y-2" key={updateKey}>
                {/* USD/TRY */}
                {rates.USDTRY && (
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-green-600 dark:text-green-400">$</span>
                      <span className="text-sm text-gray-900 dark:text-white">USD/TRY</span>
                    </div>
                    <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                      {formatRate(showBuyRates ? rates.USDTRY.buy : rates.USDTRY.sell)}
                    </span>
                  </div>
                )}

                {/* EUR/TRY */}
                {rates.EURTRY && (
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400">€</span>
                      <span className="text-sm text-gray-900 dark:text-white">EUR/TRY</span>
                    </div>
                    <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                      {formatRate(showBuyRates ? rates.EURTRY.buy : rates.EURTRY.sell)}
                    </span>
                  </div>
                )}

                {/* EUR/USD */}
                {rates.EURUSD && (
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-purple-600 dark:text-purple-400">€/$</span>
                      <span className="text-sm text-gray-900 dark:text-white">EUR/USD</span>
                    </div>
                    <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                      {formatRate(showBuyRates ? rates.EURUSD.buy : rates.EURUSD.sell)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Footer - API timestamp kullan */}
            {!loading && !error && Object.keys(rates).length > 0 && (
              <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  {rates.USDTRY?.timestamp && (
                    <>Son güncelleme: {formatTurkishTime(rates.USDTRY.timestamp)}</>
                  )}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                  Harem Altın
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrencyDisplay;
